"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebsocketServer = void 0;
const http_1 = require("http");
const https_1 = require("https");
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const ws_1 = __importDefault(require("ws"));
const WebsocketServerConnection_1 = require("./WebsocketServerConnection");
const utils_1 = require("@streamr/utils");
const autocertifier_client_1 = require("@streamr/autocertifier-client");
const errors_1 = require("../../helpers/errors");
const range_1 = __importDefault(require("lodash/range"));
const fs_1 = __importDefault(require("fs"));
const uuid_1 = require("uuid");
const url_1 = require("url");
const logger = new utils_1.Logger(module);
class WebsocketServer extends eventemitter3_1.default {
    httpServer;
    wsServer;
    abortController = new AbortController();
    options;
    constructor(options) {
        super();
        this.options = options;
    }
    async start() {
        const ports = (0, range_1.default)(this.options.portRange.min, this.options.portRange.max + 1);
        for (const port of ports) {
            try {
                await (0, utils_1.asAbortable)(this.startServer(port, this.options.enableTls), this.abortController.signal);
                return port;
            }
            catch (err) {
                if (err.originalError?.code === 'EADDRINUSE') {
                    logger.debug(`failed to start WebSocket server on port: ${port} reattempting on next port`);
                }
                else {
                    throw new errors_1.WebsocketServerStartError(err);
                }
            }
        }
        throw new errors_1.WebsocketServerStartError(`Failed to start WebSocket server on any port in range: ${this.options.portRange.min}-${this.options.portRange.min}`);
    }
    // If tlsCertificate has been given the tls boolean is ignored
    // TODO: could be simplified?
    startServer(port, tls) {
        const requestListener = (request, response) => {
            logger.trace('Received request for ' + request.url);
            response.writeHead(404);
            response.end();
        };
        return new Promise((resolve, reject) => {
            if (this.options.tlsCertificate) {
                this.httpServer = (0, https_1.createServer)({
                    key: fs_1.default.readFileSync(this.options.tlsCertificate.privateKeyFileName),
                    cert: fs_1.default.readFileSync(this.options.tlsCertificate.certFileName)
                }, requestListener);
            }
            else if (!tls) {
                this.httpServer = (0, http_1.createServer)(requestListener);
            }
            else {
                // TODO use options option or named constant?
                const certificate = (0, autocertifier_client_1.createSelfSignedCertificate)('streamr-self-signed-' + (0, uuid_1.v4)(), 1000);
                this.httpServer = (0, https_1.createServer)({
                    key: certificate.serverKey,
                    cert: certificate.serverCert
                }, requestListener);
            }
            function originIsAllowed() {
                return true;
            }
            this.wsServer = this.createWsServer();
            this.wsServer.on('connection', (ws, request) => {
                logger.trace(`New connection from ${request.socket.remoteAddress}`);
                if (!originIsAllowed()) {
                    // Make sure we only accept requests from an allowed origin
                    ws.close();
                    logger.trace('IConnection from origin ' + request.headers.origin + ' rejected.');
                    return;
                }
                this.emit('connected', new WebsocketServerConnection_1.WebsocketServerConnection(ws, (0, url_1.parse)(request.url), request.socket.remoteAddress));
            });
            this.httpServer.on('upgrade', (request, socket, head) => {
                logger.trace('Received upgrade request for ' + request.url);
                this.wsServer.handleUpgrade(request, socket, head, (ws) => {
                    this.wsServer.emit('connection', ws, request);
                });
            });
            this.httpServer.once('error', (err) => {
                reject(new errors_1.WebsocketServerStartError('Starting Websocket server failed', err));
            });
            this.httpServer.once('listening', () => {
                logger.debug('Websocket server is listening on port ' + port);
                resolve();
            });
            try {
                // Listen only to IPv4 network interfaces, default value listens to IPv6 as well
                this.httpServer.listen(port, '0.0.0.0');
            }
            catch (e) {
                reject(new errors_1.WebsocketServerStartError('Websocket server threw an exception', e));
            }
        });
    }
    updateCertificate(cert, key) {
        this.httpServer.setSecureContext({
            cert,
            key
        });
    }
    stop() {
        this.abortController.abort();
        this.removeAllListeners();
        return new Promise((resolve, _reject) => {
            this.wsServer.close();
            for (const ws of this.wsServer.clients) {
                ws.terminate();
            }
            this.httpServer?.once('close', () => {
                // removeAllListeners is maybe not needed?
                this.httpServer?.removeAllListeners();
                resolve();
            });
            this.httpServer?.close();
            // the close method "Stops the server from accepting new connections and closes all 
            // connections connected to this server which are not sending a request or waiting for a 
            // response." (https://nodejs.org/api/http.html#serverclosecallback)
            // i.e. we need to call closeAllConnections() to close the rest of the connections
            // (in practice this closes the active websocket connections)
            this.httpServer?.closeAllConnections();
        });
    }
    createWsServer() {
        const maxPayload = this.options.maxMessageSize ?? 1048576;
        return this.wsServer = new ws_1.default.Server({
            noServer: true,
            maxPayload
        });
    }
}
exports.WebsocketServer = WebsocketServer;
//# sourceMappingURL=WebsocketServer.js.map